import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Building2, Loader2, Plus, RefreshCw, MessageSquare, Users as UsersIcon,
  CheckCircle2, Trash2, Pencil, Play, StopCircle, KeyRound, X, AlertTriangle, Sparkles,
  LayoutDashboard, TrendingUp, Activity, ShoppingBag, Send,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "offices", label: "Uffici", icon: Building2 },
  { id: "users", label: "Utenti", icon: UsersIcon },
  { id: "requests", label: "Richieste", icon: MessageSquare },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [officeDialog, setOfficeDialog] = useState(null); // { mode: 'create'|'edit', office?: o }
  const [userDialog, setUserDialog] = useState(null); // { mode: 'create'|'edit', user?: u, prefillOfficeId? }
  const [approveDialog, setApproveDialog] = useState(null); // { request: r }
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, body, onConfirm, danger? }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, u, r, s] = await Promise.all([
        api.get("/superadmin/offices"),
        api.get("/superadmin/users"),
        api.get("/superadmin/contact-requests"),
        api.get("/superadmin/stats"),
      ]);
      setOffices(o.data);
      setUsers(u.data);
      setRequests(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const newRequestsCount = requests.filter((r) => r.status === "new").length;

  // ---------- Bot start/stop ----------
  const toggleBot = async (office) => {
    if (!office.telegram_bot_configured) {
      toast.error("Configura prima il token del bot");
      return;
    }
    const action = office.bot_running ? "stop" : "start";
    try {
      await api.post(`/superadmin/offices/${office.id}/bot/${action}`);
      toast.success(office.bot_running ? "Bot fermato" : "Bot avviato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---------- Delete handlers ----------
  const askDeleteOffice = (office) => {
    const hasData = office.users_count > 0 || office.dishes_count > 0;
    setConfirmDialog({
      title: `Eliminare "${office.name}"?`,
      body: hasData
        ? `Questo ufficio ha ${office.users_count} utenti e ${office.dishes_count} piatti attivi. Saranno eliminati TUTTI i dati associati (utenti, piatti, ordini, template). Azione irreversibile.`
        : "L'ufficio è vuoto. Sarà eliminato definitivamente.",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/superadmin/offices/${office.id}`, { params: { cascade: true } });
          toast.success("Ufficio eliminato");
          setConfirmDialog(null);
          fetchAll();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  const askDeleteUser = (u) => {
    setConfirmDialog({
      title: `Eliminare "${u.name}"?`,
      body: `Verrà eliminato l'utente ${u.email}. Le sue prenotazioni passate restano per riferimento.`,
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/superadmin/users/${u.id}`);
          toast.success("Utente eliminato");
          setConfirmDialog(null);
          fetchAll();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  // ---------- Bulk handlers ----------
  const askBulkDeleteUsers = (ids) => {
    if (!ids.length) return;
    setConfirmDialog({
      title: `Eliminare ${ids.length} utenti?`,
      body: `Stai per eliminare ${ids.length} utenti. L'azione è irreversibile. Il tuo account non verrà toccato anche se è nella selezione.`,
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/superadmin/users/bulk-delete`, { ids });
          toast.success(`${data.deleted} utenti eliminati${data.skipped_self ? " (il tuo account è stato saltato)" : ""}`);
          setConfirmDialog(null);
          fetchAll();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  const askBulkDeleteRequests = (ids) => {
    if (!ids.length) return;
    setConfirmDialog({
      title: `Eliminare ${ids.length} richieste?`,
      body: `Stai per eliminare ${ids.length} richieste di contatto. L'azione è irreversibile.`,
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/superadmin/contact-requests/bulk-delete`, { ids });
          toast.success(`${data.deleted} richieste eliminate`);
          setConfirmDialog(null);
          fetchAll();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  const bulkUpdateRequestStatus = async (ids, status) => {
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/superadmin/contact-requests/bulk-status`, { ids, status });
      toast.success(`${data.updated} richieste → ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---------- Request status ----------
  const updateReqStatus = async (id, status) => {
    try {
      await api.put(`/superadmin/contact-requests/${id}/status?status=${status}`);
      toast.success(`Stato → ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resendWelcome = async (req) => {
    if (!req.email) {
      toast.error("Email assente nella richiesta");
      return;
    }
    const reset = window.confirm(
      `Re-inviare l'email di benvenuto a ${req.email}?\n\nOK = re-invio (la password resta quella esistente)\nAnnulla = chiudi`
    );
    if (!reset) return;
    const newPwd = window.prompt(
      "Vuoi anche RESETTARE la password? Inserisci la nuova password (min 6 caratteri).\n\nLasciare vuoto = non cambia la password.",
      ""
    );
    try {
      await api.post(`/superadmin/contact-requests/${req.id}/resend-welcome`, {
        admin_email: req.email,
        new_password: newPwd && newPwd.length >= 6 ? newPwd : null,
      });
      toast.success("Email re-inviata");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
            <Sparkles className="w-4 h-4 text-accent" /> Super Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Console di gestione
          </h1>
          <p className="text-muted-foreground mt-1.5 text-sm">
            Gestisci uffici, utenti e onboarding di nuove sedi della piattaforma.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          data-testid="refresh-super"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          const badge =
            t.id === "requests" && newRequestsCount > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 ml-1 text-[10px] font-semibold rounded-full bg-accent text-white">
                {newRequestsCount}
              </span>
            ) : null;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 -mb-px ${
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {badge}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "dashboard" && (
        <DashboardTab stats={stats} loading={loading} onJumpTab={setTab} />
      )}
      {tab === "offices" && (
        <OfficesTab
          offices={offices}
          loading={loading}
          onCreate={() => setOfficeDialog({ mode: "create" })}
          onEdit={(o) => setOfficeDialog({ mode: "edit", office: o })}
          onDelete={askDeleteOffice}
          onToggleBot={toggleBot}
          onAddUser={(o) => setUserDialog({ mode: "create", prefillOfficeId: o.id })}
        />
      )}
      {tab === "users" && (
        <UsersTab
          users={users}
          offices={offices}
          loading={loading}
          currentUserId={user?.id}
          onCreate={() => setUserDialog({ mode: "create" })}
          onEdit={(u) => setUserDialog({ mode: "edit", user: u })}
          onDelete={askDeleteUser}
          onBulkDelete={askBulkDeleteUsers}
        />
      )}
      {tab === "requests" && (
        <RequestsTab
          requests={requests}
          loading={loading}
          onStatus={updateReqStatus}
          onApprove={(r) => setApproveDialog({ request: r })}
          onResendWelcome={resendWelcome}
          onBulkDelete={askBulkDeleteRequests}
          onBulkStatus={bulkUpdateRequestStatus}
        />
      )}

      {/* Dialogs */}
      {officeDialog && (
        <OfficeDialog
          mode={officeDialog.mode}
          office={officeDialog.office}
          onClose={() => setOfficeDialog(null)}
          onSaved={() => { setOfficeDialog(null); fetchAll(); }}
        />
      )}
      {userDialog && (
        <UserDialog
          mode={userDialog.mode}
          user={userDialog.user}
          offices={offices}
          prefillOfficeId={userDialog.prefillOfficeId}
          onClose={() => setUserDialog(null)}
          onSaved={() => { setUserDialog(null); fetchAll(); }}
        />
      )}
      {approveDialog && (
        <ApproveRequestDialog
          request={approveDialog.request}
          onClose={() => setApproveDialog(null)}
          onApproved={() => { setApproveDialog(null); fetchAll(); }}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          body={confirmDialog.body}
          danger={confirmDialog.danger}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
      <SaStyles />
    </div>
  );
}

// ============================================================================
// Dashboard Tab
// ============================================================================
function DashboardTab({ stats, loading, onJumpTab }) {
  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }
  const k = stats.kpi || {};
  const trend = stats.trend_orders_14d || [];
  const top = stats.top_offices_week || [];

  return (
    <section data-testid="dashboard-content">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi
          icon={Building2}
          label="Uffici"
          value={k.total_offices}
          hint={`${k.bots_running}/${k.bots_configured} bot attivi`}
          testid="kpi-offices"
          onClick={() => onJumpTab("offices")}
        />
        <Kpi
          icon={UsersIcon}
          label="Utenti totali"
          value={k.total_users}
          testid="kpi-users"
          onClick={() => onJumpTab("users")}
        />
        <Kpi
          icon={ShoppingBag}
          label="Prenotazioni oggi"
          value={k.orders_today}
          hint={`${k.orders_week} questa settimana`}
          testid="kpi-orders-today"
        />
        <Kpi
          icon={MessageSquare}
          label="Richieste nuove"
          value={k.new_requests}
          hint={k.new_requests > 0 ? "Da gestire" : "Tutto in ordine"}
          accent={k.new_requests > 0}
          testid="kpi-requests"
          onClick={() => onJumpTab("requests")}
        />
      </div>

      {/* Second row: secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8">
        <SmallStat icon={Activity} label="Bot online" value={`${k.bots_running}/${k.total_offices}`} testid="kpi-bots-online" />
        <SmallStat icon={TrendingUp} label="Prenotazioni totali" value={k.orders_total} testid="kpi-orders-total" />
        <SmallStat icon={Sparkles} label="Piatti attivi" value={k.total_dishes_active} testid="kpi-dishes-active" />
      </div>

      {/* Trend chart */}
      <div className="bg-card border border-border rounded-lg p-5 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Ultimi 14 giorni
            </div>
            <h3 className="font-display text-lg font-medium">Prenotazioni giornaliere</h3>
          </div>
          <div className="text-xs text-muted-foreground">
            Tot. periodo: <b className="text-foreground">{trend.reduce((s, t) => s + t.count, 0)}</b>
          </div>
        </div>
        <TrendChart data={trend} />
      </div>

      {/* Top offices */}
      <div className="bg-card border border-border rounded-lg p-5 sm:p-6">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Top sedi · questa settimana
          </div>
          <h3 className="font-display text-lg font-medium">Più attive negli ultimi 7 giorni</h3>
        </div>
        {top.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Nessuna prenotazione negli ultimi 7 giorni.</div>
        ) : (
          <ul className="space-y-2.5" data-testid="top-offices-list">
            {top.map((o, i) => {
              const max = top[0]?.orders || 1;
              const pct = (o.orders / max) * 100;
              return (
                <li key={o.office_id} className="flex items-center gap-3">
                  <span className="w-6 text-xs text-muted-foreground tabular-nums">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span className="text-sm font-medium truncate">{o.office_name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {o.orders} pren. · {o.portions} porz.
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, hint, accent, testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-card border rounded-lg p-4 sm:p-5 ${
        accent ? "border-accent/40 bg-accent/5" : "border-border"
      } ${onClick ? "hover:border-foreground/30 cursor-pointer transition-colors" : ""}`}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${
        accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"
      }`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="text-2xl sm:text-3xl font-display font-semibold tracking-tight tabular-nums">{value ?? "—"}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {hint && <div className="text-[11px] text-muted-foreground/80 mt-1">{hint}</div>}
    </Tag>
  );
}

function SmallStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3 sm:p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-8 h-8 rounded-md bg-muted text-muted-foreground flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-display font-semibold tabular-nums leading-none">{value ?? "—"}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function TrendChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">Nessun dato.</div>;
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  const W = 560;
  const H = 140;
  const padX = 8;
  const padY = 10;
  const barW = (W - padX * 2) / data.length - 4;
  return (
    <div className="overflow-x-auto" data-testid="trend-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" height={160}>
        {/* grid lines */}
        {[0.25, 0.5, 0.75].map((g) => (
          <line
            key={g} x1={padX} x2={W - padX}
            y1={padY + (H - padY * 2) * g} y2={padY + (H - padY * 2) * g}
            stroke="currentColor" strokeOpacity={0.05}
          />
        ))}
        {data.map((d, i) => {
          const h = (d.count / max) * (H - padY * 2);
          const x = padX + i * ((W - padX * 2) / data.length) + 2;
          const y = H - padY - h;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={barW} height={h} rx={2}
                fill="hsl(120 18% 41%)" opacity={0.85}>
                <title>{d.date}: {d.count} prenotazioni</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1 px-1">
        <span>{shortDay(data[0]?.date)}</span>
        <span>{shortDay(data[Math.floor(data.length / 2)]?.date)}</span>
        <span>{shortDay(data[data.length - 1]?.date)}</span>
      </div>
    </div>
  );
}
function shortDay(s) {
  if (!s) return "";
  const [, m, d] = s.split("-");
  return `${d}/${m}`;
}

// ============================================================================
// Offices Tab
// ============================================================================
function OfficesTab({ offices, loading, onCreate, onEdit, onDelete, onToggleBot, onAddUser }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {offices.length} {offices.length === 1 ? "ufficio attivo" : "uffici attivi"}
        </p>
        <button
          onClick={onCreate}
          data-testid="new-office-button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343]"
        >
          <Plus className="w-3.5 h-3.5" /> Nuovo ufficio
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : offices.length === 0 ? (
        <EmptyCard testid="offices-empty">Nessun ufficio configurato.</EmptyCard>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {offices.map((o) => (
            <article
              key={o.id}
              className="bg-card border border-border rounded-lg p-5 flex flex-col"
              data-testid={`office-card-${o.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-display font-medium text-lg truncate">{o.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    /{o.slug} {o.location && <>· {o.location}</>}
                  </div>
                </div>
                <BotStatusBadge office={o} />
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
                <div>Contatto: {o.contact_email || "—"}</div>
                <div>Cutoff predef.: {o.default_cutoff_time}</div>
                {o.telegram_bot_username && (
                  <div>Bot: <code className="text-primary">@{o.telegram_bot_username}</code></div>
                )}
                <div>👤 {o.users_count} utenti · 🍽 {o.dishes_count} piatti attivi</div>
                {!o.is_public && <div className="text-amber-700">⚠ Privato (non in lista pubblica)</div>}
                {o.is_protected && <div className="text-primary">🔒 Sede principale protetta</div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-3 border-t border-border">
                <ActionBtn onClick={() => onEdit(o)} icon={Pencil} label="Modifica" testid={`office-edit-${o.slug}`} />
                <ActionBtn
                  onClick={() => onToggleBot(o)}
                  icon={o.bot_running ? StopCircle : Play}
                  label={o.bot_running ? "Ferma bot" : "Avvia bot"}
                  disabled={!o.telegram_bot_configured}
                  variant={o.bot_running ? "warning" : "primary"}
                  testid={`office-bot-toggle-${o.slug}`}
                />
                <ActionBtn onClick={() => onAddUser(o)} icon={Plus} label="Aggiungi utente" testid={`office-add-user-${o.slug}`} />
                <ActionBtn
                  onClick={() => onDelete(o)}
                  icon={Trash2}
                  label={o.is_protected ? "Protetto" : "Elimina"}
                  variant="danger"
                  disabled={o.is_protected}
                  testid={`office-delete-${o.slug}`}
                />
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function BotStatusBadge({ office }) {
  if (office.bot_running) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide bg-primary/10 text-primary rounded shrink-0">
        <CheckCircle2 className="w-3 h-3" /> Bot live
      </span>
    );
  }
  if (office.telegram_bot_configured) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 rounded shrink-0">Bot fermo</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide bg-muted text-muted-foreground rounded shrink-0">No token</span>;
}

// ============================================================================
// Users Tab
// ============================================================================
function UsersTab({ users, offices, loading, currentUserId, onCreate, onEdit, onDelete, onBulkDelete }) {
  const [filterOffice, setFilterOffice] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterOffice && u.office_id !== filterOffice) return false;
      if (filterRole && u.role !== filterRole) return false;
      if (q && !(`${u.name} ${u.email}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [users, filterOffice, filterRole, search]);

  // Selectable = filtered minus current user (cannot be deleted)
  const selectableIds = useMemo(
    () => filtered.filter((u) => u.id !== currentUserId).map((u) => u.id),
    [filtered, currentUserId]
  );

  // Drop selections that are no longer visible after filtering
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(selectableIds);
      const next = new Set();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [selectableIds]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = selectableIds.length > 0 && selectedIds.size === selectableIds.length;
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
  };
  const clearSelection = () => setSelectedIds(new Set());

  return (
    <section>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {filtered.length} / {users.length} utenti
        </p>
        <button
          onClick={onCreate}
          data-testid="new-user-button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343]"
        >
          <Plus className="w-3.5 h-3.5" /> Nuovo utente
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome o email…"
          data-testid="users-search"
          className="oinput-sa"
        />
        <select value={filterOffice} onChange={(e) => setFilterOffice(e.target.value)} data-testid="users-filter-office" className="oinput-sa">
          <option value="">Tutti gli uffici</option>
          {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="users-filter-role" className="oinput-sa">
          <option value="">Tutti i ruoli</option>
          <option value="superadmin">superadmin</option>
          <option value="admin">admin</option>
          <option value="preparatore">preparatore</option>
          <option value="ordinante">ordinante</option>
        </select>
      </div>

      {selectedIds.size > 0 && (
        <BulkBar
          testid="users-bulk-bar"
          count={selectedIds.size}
          label={`utente${selectedIds.size === 1 ? "" : "i"}`}
          onClear={clearSelection}
          actions={[
            {
              label: "Elimina selezionati",
              testid: "users-bulk-delete",
              danger: true,
              icon: Trash2,
              onClick: () => onBulkDelete(Array.from(selectedIds)),
            },
          ]}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyCard testid="users-empty">Nessun utente con questi filtri.</EmptyCard>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5 w-9">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={selectableIds.length === 0}
                      data-testid="users-select-all"
                      aria-label="Seleziona tutti gli utenti visibili"
                      className="cursor-pointer accent-[#557555]"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium">Nome</th>
                  <th className="text-left px-4 py-2.5 font-medium">Email</th>
                  <th className="text-left px-4 py-2.5 font-medium">Ufficio</th>
                  <th className="text-left px-4 py-2.5 font-medium">Ruolo</th>
                  <th className="text-left px-4 py-2.5 font-medium">Telegram</th>
                  <th className="text-right px-4 py-2.5 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelf = u.id === currentUserId;
                  const checked = selectedIds.has(u.id);
                  return (
                    <tr
                      key={u.id}
                      className={`border-t border-border ${checked ? "bg-primary/5" : ""}`}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(u.id)}
                          disabled={isSelf}
                          data-testid={`user-select-${u.id}`}
                          aria-label={`Seleziona ${u.name}`}
                          className="cursor-pointer accent-[#557555] disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </td>
                      <td className="px-4 py-2.5">{u.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-2.5">
                        {u.office?.name || <span className="text-muted-foreground italic">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {u.telegram_chat_id ? <span className="text-primary">collegato</span> : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex gap-1">
                          <IconBtn onClick={() => onEdit(u)} icon={Pencil} label="Modifica" testid={`user-edit-${u.id}`} />
                          <IconBtn
                            onClick={() => onDelete(u)}
                            icon={Trash2}
                            label="Elimina"
                            danger
                            disabled={isSelf}
                            testid={`user-delete-${u.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function RoleBadge({ role }) {
  const map = {
    superadmin: "bg-accent/15 text-accent",
    admin: "bg-primary/15 text-primary",
    preparatore: "bg-amber-100 text-amber-700",
    ordinante: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-medium ${map[role] || map.ordinante}`}>
      {role}
    </span>
  );
}

// ============================================================================
// Requests Tab
// ============================================================================
function RequestsTab({ requests, loading, onStatus, onApprove, onResendWelcome, onBulkDelete, onBulkStatus }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const allIds = useMemo(() => requests.map((r) => r.id), [requests]);

  // Keep selection consistent with the current list
  useEffect(() => {
    setSelectedIds((prev) => {
      const valid = new Set(allIds);
      const next = new Set();
      prev.forEach((id) => { if (valid.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [allIds]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = allIds.length > 0 && selectedIds.size === allIds.length;
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));
  const clearSelection = () => setSelectedIds(new Set());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }
  if (requests.length === 0) return <EmptyCard testid="requests-empty">Nessuna richiesta di contatto.</EmptyCard>;

  return (
    <div>
      {/* Master select + bulk bar */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            data-testid="requests-select-all"
            aria-label="Seleziona tutte le richieste"
            className="cursor-pointer accent-[#557555]"
          />
          Seleziona tutte ({requests.length})
        </label>
      </div>
      {selectedIds.size > 0 && (
        <BulkBar
          testid="requests-bulk-bar"
          count={selectedIds.size}
          label={`richiest${selectedIds.size === 1 ? "a" : "e"}`}
          onClear={clearSelection}
          actions={[
            {
              label: "Segna visto",
              testid: "requests-bulk-seen",
              icon: CheckCircle2,
              onClick: () => { onBulkStatus(Array.from(selectedIds), "seen"); clearSelection(); },
            },
            {
              label: "Segna gestito",
              testid: "requests-bulk-handled",
              icon: CheckCircle2,
              onClick: () => { onBulkStatus(Array.from(selectedIds), "handled"); clearSelection(); },
            },
            {
              label: "Elimina selezionati",
              testid: "requests-bulk-delete",
              danger: true,
              icon: Trash2,
              onClick: () => onBulkDelete(Array.from(selectedIds)),
            },
          ]}
        />
      )}
      <div className="bg-card border border-border rounded-lg divide-y divide-border">
        {requests.map((r) => (
          <RequestRow
            key={r.id}
            req={r}
            selected={selectedIds.has(r.id)}
            onToggle={() => toggleOne(r.id)}
            onStatus={onStatus}
            onApprove={onApprove}
            onResendWelcome={onResendWelcome}
          />
        ))}
      </div>
    </div>
  );
}

const D = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function RequestRow({ req, selected, onToggle, onStatus, onApprove, onResendWelcome }) {
  const isNew = req.status === "new";
  const isHandled = req.status === "handled";
  const planLabels = { starter: "Starter", pro: "Pro", custom: "Enterprise" };
  return (
    <div
      className={`p-4 sm:p-5 ${selected ? "bg-primary/10" : isNew ? "bg-primary/5" : ""}`}
      data-testid={`request-${req.id}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggle}
          data-testid={`request-select-${req.id}`}
          aria-label={`Seleziona richiesta ${req.office_name}`}
          className="cursor-pointer accent-[#557555] mt-1 shrink-0"
        />
        <div className="flex items-start justify-between gap-3 flex-wrap flex-1 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="font-medium">
              {req.office_name}
              {req.location && <span className="text-muted-foreground font-normal"> · {req.location}</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {req.name} ({req.role_hint || "—"}) · <a href={`mailto:${req.email}`} className="text-primary">{req.email}</a> · {D.format(new Date(req.created_at))}
            </div>
            <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
              {req.plan_interest && planLabels[req.plan_interest] && (
                <span className="px-2 py-0.5 bg-accent/10 text-accent rounded font-medium" data-testid={`request-plan-${req.id}`}>
                  💼 {planLabels[req.plan_interest]}
                </span>
              )}
              {req.headcount > 0 && (
                <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded">
                  👥 ~{req.headcount} dipendenti
                </span>
              )}
              {req.welcome_email_sent && (
                <span className="px-2 py-0.5 bg-primary/10 text-primary rounded" title={req.welcome_email_at || ""}>
                  ✉️ email inviata
                </span>
              )}
            </div>
            {req.message && <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{req.message}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${
              isNew ? "bg-accent text-white" :
              req.status === "seen" ? "bg-amber-100 text-amber-700" :
              "bg-muted text-muted-foreground"
            }`}>{req.status}</span>
            {!isHandled && (
              <button
                onClick={() => onApprove(req)}
                data-testid={`request-approve-${req.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-md hover:bg-[#436343]"
              >
                <CheckCircle2 className="w-3 h-3" /> Approva & crea
              </button>
            )}
            {isHandled && (
              <button
                onClick={() => onResendWelcome(req)}
                data-testid={`request-resend-${req.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-primary/30 text-primary text-xs font-medium rounded-md hover:bg-primary/5"
              >
                <Send className="w-3 h-3" /> Re-invia email
              </button>
            )}
            <select
              value={req.status}
              onChange={(e) => onStatus(req.id, e.target.value)}
              data-testid={`request-status-${req.id}`}
              className="text-xs px-2 py-1 bg-white border border-border rounded-md"
            >
              <option value="new">nuovo</option>
              <option value="seen">visto</option>
              <option value="handled">gestito</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// Generic bulk action bar shown when selection is non-empty.
function BulkBar({ count, label, actions, onClear, testid }) {
  return (
    <div
      data-testid={testid}
      className="sticky top-2 z-20 mb-4 flex items-center justify-between gap-3 px-4 py-2.5 bg-foreground text-white rounded-lg shadow-lg flex-wrap"
    >
      <div className="flex items-center gap-3 text-sm">
        <span className="font-medium">{count} {label} selezionat{count === 1 ? "o" : "i"}</span>
        <button
          onClick={onClear}
          data-testid={`${testid}-clear`}
          className="text-xs text-white/70 hover:text-white underline-offset-2 hover:underline"
        >
          Annulla
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.label}
              onClick={a.onClick}
              data-testid={a.testid}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                a.danger
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />} {a.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Dialogs
// ============================================================================
function DialogShell({ title, subtitle, onClose, children, maxWidth = "max-w-xl" }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className={`bg-card border border-border rounded-lg shadow-xl w-full ${maxWidth} max-h-[90vh] flex flex-col`} data-testid="dialog">
        <header className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold">{title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="dialog-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

function OfficeDialog({ mode, office, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: office?.name || "",
    slug: office?.slug || "",
    location: office?.location || "",
    contact_email: office?.contact_email || "",
    default_cutoff_time: office?.default_cutoff_time || "11:30",
    is_public: office?.is_public ?? true,
    telegram_bot_token: "",  // never prefill secret
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // For edit: only send token if user typed something
      const payload = { ...form };
      if (isEdit && !payload.telegram_bot_token) delete payload.telegram_bot_token;
      if (isEdit) {
        await api.put(`/superadmin/offices/${office.id}`, payload);
        toast.success("Ufficio aggiornato");
      } else {
        await api.post("/superadmin/offices", payload);
        toast.success("Ufficio creato");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell
      title={isEdit ? `Modifica "${office.name}"` : "Nuovo ufficio"}
      subtitle={isEdit ? `/${office.slug}` : "Crea un nuovo ufficio sulla piattaforma"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="office-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome" required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="office-name" className="oinput-sa" />
          </Field>
          <Field label="Slug (URL)" required>
            <input
              required pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
              data-testid="office-slug"
              placeholder="es. milano-tribunale"
              className="oinput-sa"
            />
          </Field>
          <Field label="Città / Sede">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="office-location" className="oinput-sa" />
          </Field>
          <Field label="Email di contatto">
            <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="office-email" className="oinput-sa" />
          </Field>
          <Field label="Cutoff predefinito">
            <input type="time" value={form.default_cutoff_time} onChange={(e) => setForm({ ...form, default_cutoff_time: e.target.value })} className="oinput-sa" />
          </Field>
          <div className="pt-7">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={form.is_public}
                onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                data-testid="office-is-public"
              />
              Visibile nella lista pubblica
            </label>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <Field label={
            <>
              <KeyRound className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
              Token Telegram Bot {isEdit && <span className="text-xs text-muted-foreground font-normal">(lascia vuoto per non cambiare)</span>}
            </>
          }>
            <input
              type="text"
              value={form.telegram_bot_token}
              onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
              data-testid="office-bot-token"
              placeholder={isEdit && office?.telegram_bot_configured ? "•••••••• (token impostato)" : "Ottieni da @BotFather"}
              className="oinput-sa font-mono text-xs"
            />
          </Field>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Inserendo un token valido qui, il bot dell'ufficio verrà avviato automaticamente. Per rimuoverlo, premi "Modifica" e inserisci uno spazio.
          </p>
        </div>

        <DialogActions>
          <button type="button" onClick={onClose} className="btn-sa-secondary">Annulla</button>
          <button type="submit" disabled={saving} data-testid="office-submit" className="btn-sa-primary">
            {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea ufficio"}
          </button>
        </DialogActions>
      </form>
    </DialogShell>
  );
}

function UserDialog({ mode, user, offices, prefillOfficeId, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "ordinante",
    office_id: user?.office_id || prefillOfficeId || (offices[0]?.id || ""),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role, office_id: form.office_id };
        if (form.password) payload.password = form.password;
        await api.put(`/superadmin/users/${user.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error("Password obbligatoria (min 6 caratteri)");
          setSaving(false);
          return;
        }
        await api.post("/superadmin/users", form);
        toast.success("Utente creato");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell
      title={isEdit ? `Modifica utente "${user.name}"` : "Nuovo utente"}
      subtitle={isEdit ? user.email : "Crea utente assegnandolo a un ufficio e un ruolo"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="user-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome" required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" className="oinput-sa" />
          </Field>
          <Field label="Email" required>
            <input
              required type="email" value={form.email} disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-email" className="oinput-sa disabled:opacity-60"
            />
          </Field>
          <Field label="Ufficio" required>
            <select required value={form.office_id} onChange={(e) => setForm({ ...form, office_id: e.target.value })} data-testid="user-office" className="oinput-sa">
              <option value="">— Seleziona —</option>
              {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Ruolo" required>
            <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" className="oinput-sa">
              <option value="ordinante">ordinante</option>
              <option value="preparatore">preparatore</option>
              <option value="admin">admin (dell'ufficio)</option>
              <option value="superadmin">superadmin (piattaforma)</option>
            </select>
          </Field>
        </div>
        <Field label={isEdit ? "Nuova password (lascia vuoto per non cambiarla)" : "Password (min 6)"}>
          <input
            type="password" minLength={isEdit ? 0 : 6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="user-password" className="oinput-sa"
            placeholder={isEdit ? "••••••" : ""}
          />
        </Field>
        <DialogActions>
          <button type="button" onClick={onClose} className="btn-sa-secondary">Annulla</button>
          <button type="submit" disabled={saving} data-testid="user-submit" className="btn-sa-primary">
            {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea utente"}
          </button>
        </DialogActions>
      </form>
    </DialogShell>
  );
}

function ApproveRequestDialog({ request, onClose, onApproved }) {
  const suggestedSlug = (request.office_name || "").toLowerCase()
    .replace(/[àáâã]/g, "a").replace(/[èéê]/g, "e").replace(/[ìí]/g, "i")
    .replace(/[òóô]/g, "o").replace(/[ùú]/g, "u")
    .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 40);
  const [form, setForm] = useState({
    office_name: request.office_name,
    office_slug: suggestedSlug,
    office_location: request.location || "",
    office_contact_email: request.email,
    default_cutoff_time: "11:30",
    is_public: true,
    telegram_bot_token: "",
    admin_name: request.name,
    admin_email: request.email,
    admin_password: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.admin_password || form.admin_password.length < 6) {
      toast.error("La password admin deve avere almeno 6 caratteri");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/superadmin/contact-requests/${request.id}/approve`, form);
      toast.success("Ufficio creato e admin attivato 🎉");
      onApproved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell
      title="Approva richiesta e crea sede"
      subtitle={`${request.name} · ${request.office_name}`}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-5" data-testid="approve-form">
        <Section title="Dettagli ufficio">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nome ufficio" required>
              <input required value={form.office_name} onChange={(e) => setForm({ ...form, office_name: e.target.value })} data-testid="approve-office-name" className="oinput-sa" />
            </Field>
            <Field label="Slug (URL)" required>
              <input
                required pattern="[a-z0-9]+(-[a-z0-9]+)*"
                value={form.office_slug}
                onChange={(e) => setForm({ ...form, office_slug: e.target.value.toLowerCase() })}
                data-testid="approve-office-slug" className="oinput-sa"
              />
            </Field>
            <Field label="Città / Sede">
              <input value={form.office_location} onChange={(e) => setForm({ ...form, office_location: e.target.value })} className="oinput-sa" />
            </Field>
            <Field label="Email contatto">
              <input type="email" value={form.office_contact_email} onChange={(e) => setForm({ ...form, office_contact_email: e.target.value })} className="oinput-sa" />
            </Field>
            <Field label="Cutoff predefinito">
              <input type="time" value={form.default_cutoff_time} onChange={(e) => setForm({ ...form, default_cutoff_time: e.target.value })} className="oinput-sa" />
            </Field>
            <div className="pt-7">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
                Visibile nella lista pubblica
              </label>
            </div>
          </div>
          <Field label="Token Telegram Bot (opzionale, impostabile dopo)">
            <input
              type="text" value={form.telegram_bot_token}
              onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
              data-testid="approve-bot-token"
              placeholder="Ottieni da @BotFather"
              className="oinput-sa font-mono text-xs"
            />
          </Field>
        </Section>

        <Section title="Admin founder (responsabile dell'ufficio)">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nome" required>
              <input required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="approve-admin-name" className="oinput-sa" />
            </Field>
            <Field label="Email" required>
              <input required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="approve-admin-email" className="oinput-sa" />
            </Field>
            <Field label="Password iniziale (min 6)" required>
              <input
                required type="password" minLength={6}
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                data-testid="approve-admin-password" className="oinput-sa"
              />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground bg-primary/5 border border-primary/15 rounded-md p-3 mt-3 leading-relaxed">
            💡 Comunica queste credenziali all'admin via email separata. Potrà gestire utenti, piatti e bot del suo ufficio.
          </p>
        </Section>

        <DialogActions>
          <button type="button" onClick={onClose} className="btn-sa-secondary">Annulla</button>
          <button type="submit" disabled={saving} data-testid="approve-submit" className="btn-sa-primary">
            {saving ? "Creazione…" : "Crea ufficio + admin"}
          </button>
        </DialogActions>
      </form>
    </DialogShell>
  );
}

function ConfirmDialog({ title, body, danger, onCancel, onConfirm }) {
  return (
    <DialogShell title={title} onClose={onCancel} maxWidth="max-w-md">
      <div className="flex items-start gap-3 mb-4">
        {danger && <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />}
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      </div>
      <DialogActions>
        <button type="button" onClick={onCancel} className="btn-sa-secondary" data-testid="confirm-cancel">Annulla</button>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="confirm-ok"
          className={danger ? "btn-sa-danger" : "btn-sa-primary"}
        >
          {danger ? "Sì, elimina" : "Conferma"}
        </button>
      </DialogActions>
    </DialogShell>
  );
}

// ============================================================================
// Atoms
// ============================================================================
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 text-foreground/80">
        {label}{required && <span className="text-accent ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
function DialogActions({ children }) {
  return <div className="flex justify-end gap-2 pt-2">{children}</div>;
}
function EmptyCard({ children, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm" data-testid={testid}>
      {children}
    </div>
  );
}
function ActionBtn({ onClick, icon: Icon, label, testid, disabled, variant = "default" }) {
  const styles = {
    default: "border-border text-foreground hover:bg-muted",
    primary: "border-primary/30 text-primary hover:bg-primary/5",
    warning: "border-amber-300 text-amber-700 hover:bg-amber-50",
    danger: "border-accent/30 text-accent hover:bg-accent/5",
  };
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${styles[variant]}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
function IconBtn({ onClick, icon: Icon, label, testid, danger, disabled }) {
  return (
    <button
      type="button" onClick={onClick} title={label} aria-label={label}
      disabled={disabled} data-testid={testid}
      className={`p-1.5 rounded-md hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed ${danger ? "text-accent hover:bg-accent/5" : "text-muted-foreground hover:text-foreground"}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// Shared styles for SuperAdmin (injected once)
function SaStyles() {
  return (
    <style>{`
      .oinput-sa {
        width: 100%;
        padding: 0.5rem 0.75rem;
        background: white;
        border: 1px solid hsl(var(--border));
        border-radius: 0.375rem;
        font-family: "IBM Plex Sans", sans-serif;
        font-size: 0.9rem;
      }
      .oinput-sa:focus {
        outline: none;
        border-color: hsl(var(--primary));
        box-shadow: 0 0 0 3px hsla(120, 18%, 41%, 0.15);
      }
      .btn-sa-primary {
        padding: 0.5rem 1rem;
        background: hsl(var(--primary));
        color: white;
        font-weight: 500;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        transition: background-color 0.15s;
      }
      .btn-sa-primary:hover { background: #436343; }
      .btn-sa-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      .btn-sa-secondary {
        padding: 0.5rem 1rem;
        background: white;
        color: hsl(var(--foreground));
        border: 1px solid hsl(var(--border));
        font-weight: 500;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        transition: background-color 0.15s;
      }
      .btn-sa-secondary:hover { background: hsl(var(--muted)); }
      .btn-sa-danger {
        padding: 0.5rem 1rem;
        background: hsl(var(--accent));
        color: white;
        font-weight: 500;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .btn-sa-danger:hover { filter: brightness(0.95); }
    `}</style>
  );
}
